import { Box, Button, Container, Heading, Text } from '@chakra-ui/react'
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ReactElement } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { makeServerProps } from '@/server/query'

import makePage from '@/client/services/pages/makePage'
import composeRichText from '@/client/services/richText/composeRichText'
import type { SerializedRichText } from '@/client/services/richText/types'
import { getAboutUrl, getPrivacyUrl } from '@/client/services/url'
import { getArticleUrl, getUrl } from '@/client/services/url/faq'
import {
  BEHAVIORALLY_TARGETED_ADVERTISING,
  CONTEXTUAL_TARGETING,
  COOKIES,
  DEIDENTIFIED,
  EDUCATIONAL_PURPOSES,
  HOMEPAGE,
  IP_ADDRESS,
  LEARNER,
  LOCAL_STORAGE,
  PERSONAL_INFORMATION,
  SELL,
  SERVER_LOGS,
  PRIVACY_TAGS as TAGS,
  THIRD_PARTY_ADVERTISING,
} from '@/client/services/url/trustSafety'

import Layout from '@/components/Layout/Layout'
import ShwLink from '@/components/ShwLink'
import LinkableDiv from '@/components/UI/LinkableDiv'
import RichText from '@/components/UI/RichTextEditor/RichText'

const LAST_UPDATED = 'January 23, 2025'

const PRIVACY_PRINCIPLES = composeRichText(ctx => [
  ctx.paragraph(
    'Schoolhouse.world is a non-profit with the mission of connecting the world',
    ' through learning. You trust us with your information to serve this goal, and',
    ' we take our responsibility seriously. We believe you should always know what',
    ' data we collect from you, how we use it, and that you should have meaningful',
    ' control over both. We want to empower users to make the best decisions about',
    ' the information that they share with us.\n'
  ),
  ctx.paragraph(
    'Our privacy policy is based on several core principles.',
    ' These principles should not be considered a legal document — the ',
    ctx.linkExternal(getPrivacyUrl('policy'), 'full privacy policy'),
    ' below is — but they are what underlie the privacy policy.\n'
  ),
  ctx.bulletedList(
    ctx.listItem(
      ctx.text('Transparency and Choice', { bold: true }),
      `: We tell you what data we collect, why we collect it, and how it's used,`,
      ' retained, and shared with others. We are transparent about our practices',
      ' so that you can make meaningful choices about how your ',
      ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
      ' is used. If we make a material change, we will provide prominent notice',
      ' by posting a notice on our service or this website page and/or we will',
      ' notify you by email (if you have provided an email address to us). See ',
      ctx.linkExternal(getPrivacyUrl(TAGS.SCHOOLHOUSES_COMMITMENT_TO_PROVIDING), 'here'),
      ' for more information.\n'
    ),
    ctx.listItem(
      ctx.text('Personal Information We Collect', { bold: true }),
      ': Please see ',
      ctx.linkExternal(getPrivacyUrl(TAGS.WHAT_INFORMATION_DOES_SCHOOLHOUSE_COLLECT), 'below'),
      ' where we detail the ',
      ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
      ' we collect from each user type, including the sources from which the',
      ' information was collected, the business purpose for which the information',
      ' was collected, and the third parties with whom we share or disclose',
      ' personal information for a business purpose.\n'
    ),
    ctx.listItem(
      ctx.text('How We Use the Information We Collect', { bold: true }),
      ': We use the ',
      ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
      ' we collect from ',
      ctx.linkExternal(LEARNER, 'learners'),
      ' (or about learners from tutors) to provide and improve the Service, for ',
      ctx.linkExternal(EDUCATIONAL_PURPOSES, 'educational purposes'),
      ', security, and safety purposes, or as allowed by law. We will not',
      ' require learners to provide more personal information than is reasonably',
      ' necessary to participate in the Service. For additional information',
      ' regarding our use of information collected from tutors and other users,',
      ' see ',
      ctx.linkExternal(getPrivacyUrl(TAGS.HOW_DOES_SCHOOLHOUSE_USE_THE_INFORMATION), 'here'),
      '.\n'
    ),
    ctx.listItem(
      ctx.text('We Never Sell Personal Information', { bold: true }),
      ': We will never ',
      ctx.linkExternal(SELL, 'sell'),
      ' or rent your ',
      ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
      ' or non-personal information. Our mission is to help you learn, not sell',
      ' your personal data as a product. We will only share or disclose personal',
      ' information as set forth in the Privacy Policy, such as with a limited set',
      ' of ',
      ctx.linkExternal(getAboutUrl('third-parties'), 'third-party service providers'),
      ' necessary to provide or develop our Services (such as database hosting)',
      ' or as required by law, and we will be transparent about who these service',
      ' providers are. We will contractually require that these service providers',
      ' process personal information in accordance with our instructions and',
      ' consistent with this Privacy Policy. We work to ensure that these service',
      ' providers employ reasonable and comprehensive data protection and security',
      ' protocols. See ',
      ctx.linkExternal(getPrivacyUrl(TAGS.WILL_SCHOOLHOUSE_SHARE_ANY), 'here'),
      ' for more details.\n'
    ),
    ctx.listItem(
      ctx.text(
        'We Never Use Learner Information for Behaviorally Targeted or Third-Party Advertising',
        { bold: true }
      ),
      ': Information collected from ',
      ctx.linkExternal(LEARNER, 'learners'),
      ' (including personal information and information collected automatically)',
      ' is never used or disclosed for ',
      ctx.linkExternal(THIRD_PARTY_ADVERTISING, 'third-party advertising'),
      '. We also do not place any ',
      ctx.linkExternal(THIRD_PARTY_ADVERTISING, 'third-party advertisements'),
      ' in learner logged-in areas of the Service. Additionally, personal',
      ' information collected from learners is never used for ',
      ctx.linkExternal(BEHAVIORALLY_TARGETED_ADVERTISING, 'behaviorally-targeted advertising'),
      ' to learners by us or any third-party. We may present ',
      ctx.linkExternal(CONTEXTUAL_TARGETING, 'contextually relevant'),
      ' educational content (not using learner personal information) for either',
      ' our own products or third-party products and services ourselves that we',
      ' believe may be of interest to a learner (e.g., our co-created content with',
      ' Wacom).\nAdditionally, as a non-profit, we are sustained through',
      ' philanthropy and may have paid content partnerships with universities and',
      ' other third parties. We will only enter partnerships that help us support',
      ' learners and tutors as part of our educational mission. Sponsored content',
      ' will always be labeled (e.g., "Sponsored by _"). We will not share any of',
      ' your personal information with these sponsors without your explicit',
      ' consent, and these sponsors do not have the ability to track or collect',
      ' data about our site visitors or users.\n'
    ),
    ctx.listItem(
      ctx.text('Data Retention', { bold: true }),
      ': We will not retain learner ',
      ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
      ' for any longer than is necessary for ',
      ctx.linkExternal(EDUCATIONAL_PURPOSES, 'educational purposes'),
      ' and legal obligations, or to provide the Service for which we receive or',
      ' collect the learner personal information.\n'
    ),
    ctx.listItem(
      ctx.text('You Own Your Personal Information', { bold: true }),
      `: We don't own any personal information you provide or we receive — you (`,
      ctx.linkExternal(LEARNER, 'learners'),
      ', tutors, and/or coaches) will own your personal information.\n'
    ),
    ctx.listItem(
      ctx.text('Security and Privacy by Design and Default', { bold: true }),
      ': We strive to use security industry best practices to protect personal',
      ' information, including encryption and other security safeguards to protect',
      ' personal information. See ',
      ctx.linkExternal(getPrivacyUrl(TAGS.HOW_DOES_SCHOOLHOUSE_PROTECT), 'here'),
      ' for more information.\n'
    ),
    ctx.listItem(
      ctx.text('Right to Access, Correction, and Deletion of Data', { bold: true }),
      ': You can request to access, correct and delete your personal information. See ',
      ctx.linkExternal(getPrivacyUrl(TAGS.SCHOOLHOUSES_COMMITMENT_TO_PROVIDING), 'here'),
      ` for more information on all user's rights, including you're the`,
      ' additional rights of data portability and the right to object or withdraw',
      ' consent.\n'
    )
  ),
])

const PRIVACY_POLICY_SECTIONS: { anchor: string; content: SerializedRichText }[] = [
  {
    anchor: TAGS.INTRODUCTION_AND_SCOPE,
    content: composeRichText(ctx => [
      ctx.heading('Introduction and Scope'),
      ctx.paragraph(
        'Schoolhouse.world (',
        ctx.text('"Schoolhouse", "us", "we"', { bold: true }),
        ', or ',
        ctx.text('"our"', { bold: true }),
        ') is a non-profit organization and operates the ',
        ctx.linkExternal('https://www.schoolhouse.world', 'schoolhouse.world'),
        ' website (',
        ctx.text('"Website"', { bold: true }),
        ') as well as the areas of the Website where users login to use the',
        ' Schoolhouse tutoring platform (the ',
        ctx.text('"Schoolhouse Platform"', { bold: true }),
        '), our mobile applications (the ',
        ctx.text('"Schoolhouse Apps"', { bold: true }),
        '), or any of our other products or services that link to this Privacy',
        ' Policy that Schoolhouse may provide now or in the future (collectively, the ',
        ctx.text('"Service(s)"', { bold: true }),
        '). Schoolhouse may be acting as both a Controller and a Processor',
        ' as those terms are defined under the European Union’s General Data Protection',
        ' Regulation, including as implemented or adopted under the laws of the United',
        ' Kingdom (',
        ctx.text('"GDPR"', { bold: true }),
        '). For more information, please see ',
        ctx.linkInternal(getPrivacyUrl(TAGS.IS_SCHOOLHOUSE_A_CONTROLLER), 'below'),
        '.'
      ),
      ctx.paragraph(
        'We respect the privacy of our website visitors, ',
        ctx.linkExternal(LEARNER, 'learners'),
        ', and tutors (referred to as ',
        ctx.text('"you"', { bold: true }),
        ' or ',
        ctx.text('"your"', { bold: true }),
        ') and the personal information you share with us.\n'
      ),
      ctx.paragraph(
        'This ',
        ctx.linkInternal(getPrivacyUrl(TAGS.POLICY), 'privacy policy'),
        ' (as posted on our Website and as amended from time to time (',
        ctx.text('"Privacy Policy"', { bold: true }),
        '), explains:'
      ),
      ctx.bulletedList(
        ctx.listItem('What information Schoolhouse collects from you (and why we collect it)'),
        ctx.listItem('How we use and share that information'),
        ctx.listItem(
          'The choices and rights you have, including how to access, update, delete your information, and take your information out of Schoolhouse.\n'
        )
      ),
      ctx.paragraph(
        'We will only collect, use, and share your ',
        ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
        ' in accordance with this Privacy Policy. This Privacy Policy applies solely',
        ' to the Schoolhouse Services and the data collected by us, or those',
        " working on our behalf. This Privacy Policy also covers Schoolhouse's",
        ' treatment of any personal information about our users that our partners',
        ' or other services share with us, for example login or other',
        ' information shared from Clever, or data imported from authorized and',
        ' approved sources. It does not apply to data collected through other',
        ' websites, products, or services that Schoolhouse does not own or',
        ' control such as third-party services you might access through links',
        ' or other features (e.g., social media buttons, email campaigns, push',
        ' or in-app notifications, or YouTube videos) on the Service. These other',
        ' services have their own privacy policies, and we encourage you to review',
        ' them before providing them with personal information.'
      ),
      ctx.paragraph(
        'Our Privacy Policy is designed to provide transparency into our privacy',
        ' practices and principles. We’ve done our best to write this policy in',
        ' simple, clear terms, but if you’re not familiar with terms like personal',
        ' information, cookies, IP address, pixel tags, and browsers, read about',
        ' these ',
        ctx.linkExternal(HOMEPAGE, 'key terms'),
        '.\n'
      ),
      ctx.paragraph(
        'To contact us, please see the ',
        ctx.linkInternal(getPrivacyUrl(TAGS.CONTACTING_SCHOOLHOUSE), 'Contacting Schoolhouse'),
        ' section below.\n'
      ),
      ctx.headingThree('Quick Reference'),
      ctx.bulletedList(
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.ACKNOWLEDGMENT_AND_CONSENT),
            'Acknowledgment and Consent'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.CHILDREN_UNDER_THE_AGE),
            'Children Under the Age of 13'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.CHANGES_TO_OUR_PRIVACY_POLICY),
            'Changes to our Privacy Policy'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.WHAT_INFORMATION_DOES_SCHOOLHOUSE_COLLECT),
            'What Information Does Schoolhouse Collect?'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.ONLINE_ANALYTICS),
            'Online Analytics, Advertising, and Do-Not Track'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.HOW_DOES_SCHOOLHOUSE_USE_THE_INFORMATION),
            'How Does Schoolhouse Use the Information It Collects?'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.WILL_SCHOOLHOUSE_SHARE_ANY),
            'Will Schoolhouse Share Any Information It Collects?'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.HOW_DOES_SCHOOLHOUSE_PROTECT),
            'How Does Schoolhouse Protect and Secure My Information?'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.HOW_LONG_DOES_SCHOOLHOUSE_KEEP),
            'How Long Does Schoolhouse Keep Information About Me?'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.SCHOOLHOUSES_COMMITMENT_TO_PROVIDING),
            `Schoolhouse's Commitments to Providing Transparency and Your Rights`
          )
        ),
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.ADDITIONAL_INFORMATION_OR_ASSISTANCE),
            'Additional Information or Assistance And Article 27 GDPR Local Representative'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.WHAT_COMMUNICATIONS_WILL_I_RECEIVE),
            'What Communications Will I Receive from Schoolhouse?'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.THIRD_PARTY_AUTHENTICATION_SERVICES),
            'Third-party Authentication Services'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(getPrivacyUrl(TAGS.THIRD_PARTY_SERVICES), 'Third-Party Services')
        ),
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.WHAT_IF_I_AM_NOT_IN_THE_US),
            'What if I am not in the United States?'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.IS_SCHOOLHOUSE_A_CONTROLLER),
            'Is Schoolhouse a Controller?'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.CALIFORNIA_PRIVACY_DISCLOSURES),
            'California Privacy Disclosures'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(getPrivacyUrl(TAGS.CONTACTING_SCHOOLHOUSE), 'Contacting Schoolhouse')
        )
      ),
      ctx.paragraph('\n'),
    ]),
  },
  {
    anchor: TAGS.ACKNOWLEDGMENT_AND_CONSENT,
    content: composeRichText(ctx => [
      ctx.heading('Acknowledgment and Consent'),
      ctx.paragraph(
        'By visiting our Website, attending a tutoring session, making a',
        ' donation, applying to become a tutor, or using our Services in any',
        ' manner, you acknowledge that you understand the terms, practices and',
        ' policies described in this Privacy Policy (and as updated from time to',
        ' time). If you do not agree with our policies and practices, you',
        ' should not use our Website or our Services. Your use of the Website',
        ' and our Services is at all times subject to our ',
        ctx.linkInternal(getAboutUrl('terms'), 'Terms of Use'),
        ' (as updated from time to time (the ',
        ctx.text('Terms', { bold: true }),
        '), which incorporates this Privacy Policy. Any capitalized terms we use',
        ' in this Privacy Policy without defining them have the definitions',
        ' given to them in the "Terms".\n'
      ),
    ]),
  },
  {
    anchor: TAGS.CHILDREN_UNDER_THE_AGE,
    content: composeRichText(ctx => [
      ctx.heading('Children Under the Age of 13'),
      ctx.paragraph(
        'The Website and Services are not directed towards individuals under the',
        ' age of thirteen (13), and we request that such individuals do not',
        ' provide personal information. Additionally, we do not knowingly collect',
        ' (or knowingly allow any third party to collect), solicit or maintain ',
        ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
        ' from anyone under the age of 13 or knowingly allow such persons to',
        ' register for our Services. If you are under 13, please do not send any',
        ' personal information about yourself (such as your name, address,',
        ' telephone number, or email address) to us. If you are the parent or',
        ' legal guardian of a child under 13 who you believe has provided',
        ' personal information to Schoolhouse, please send an email to',
        ' Schoolhouse at privacy@schoolhouse.world or contact us as set forth',
        ' in the ',
        ctx.linkInternal(getPrivacyUrl(TAGS.CONTACTING_SCHOOLHOUSE), '"Contacting Schoolhouse"'),
        ' section below.\n'
      ),
    ]),
  },
  {
    anchor: TAGS.CHANGES_TO_OUR_PRIVACY_POLICY,
    content: composeRichText(ctx => [
      ctx.heading('Changes to our Privacy Policy'),
      ctx.paragraph(
        'We are constantly working to improve our Website and Services, and we',
        ' may need to change this Privacy Policy from time to time as well. You',
        ' can see when the last update was by looking at the “Last Updated” date',
        ' at the top of this page and any updates will be effective upon posting.',
        ' We won’t reduce your rights under this Privacy Policy without your',
        ' explicit consent. If we make any material changes, we’ll provide',
        ' prominent notice by posting a notice on the Service and/or notifying',
        ' you by email (using the email address you provided), so you can',
        ' review and make sure you know about them.\n'
      ),
      ctx.paragraph(
        'We encourage you to review this Privacy Policy from time to time, to',
        ' stay informed about our collection, use, and disclosure of personal',
        ' information through the Service and Website. If you don’t agree with',
        ' any changes to the Privacy Policy, you may terminate your account.\n'
      ),
      ctx.paragraph(
        'We reserve the right to notify you of updates to our Privacy Policy as',
        ' part of communications, e.g., by email, and/or by some other means.',
        ' Please note that if you have opted not to receive legal notice emails',
        ' from us (or you have not provided us with your email address), those',
        ' legal notices will still govern your use of the Services, and you are',
        ' still responsible for reading and understanding them.\n'
      ),
    ]),
  },
  {
    anchor: TAGS.WHAT_INFORMATION_DOES_SCHOOLHOUSE_COLLECT,
    content: composeRichText(ctx => [
      ctx.heading('What Information Does Schoolhouse Collect?'),
      ctx.paragraph(
        'We collect two types of information about you: (1) information that you',
        ' voluntarily provide us by using the Service or Website (described below',
        ' under ',
        ctx.linkInternal(
          getPrivacyUrl(TAGS.WHAT_INFORMATION_DOES_SCHOOLHOUSE_COLLECT_1),
          '"Information You Provide to Us"'
        ),
        ') and (2) information collected automatically as result of your use of',
        ' the Service (described below under ',
        ctx.linkInternal(
          getPrivacyUrl(TAGS.WHAT_INFORMATION_DOES_SCHOOLHOUSE_COLLECT_2),
          '"Information Collected Automatically"'
        ),
        '). We may also receive information about you from third-party sources',
        ' (as described below under ',
        ctx.linkInternal(
          getPrivacyUrl(TAGS.WHAT_INFORMATION_DOES_SCHOOLHOUSE_COLLECT_3),
          '"Information Received from Third-Party Sources"'
        ),
        '). The types and amounts of information collected will vary depending',
        ' on whether the user is a ',
        ctx.linkExternal(LEARNER, 'learner'),
        ', tutor, coach (teacher/parent), or general website visitor and how',
        ' they use Schoolhouse (e.g., if a tutor wants a certification, we may',
        ' need to collect certain grade and transcript information).\n'
      ),
      ctx.headingThree('Information You Provide to Us'),
      ctx.paragraph(
        'We ask for and collect the following ',
        ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
        ' about you when you use the Service or Website. This information is',
        ' necessary for the adequate performance of the contract between you and',
        ' us, for our legitimate interest in being able to provide and improve',
        ' the Service, and to allow us to comply with our legal obligations.',
        ' Without it, we may not be able to provide you with all the requested',
        ' services.\n'
      ),
      ctx.paragraph(
        'There are currently three categories of users on our Service: ',
        ctx.linkExternal(LEARNER, 'learners'),
        ', tutors and coaches (parents/teachers). Additionally, as a non-logged-in',
        ' visitor to the Schoolhouse Website, you may provide personal information',
        ' to us as set forth below.\n'
      ),
      ctx.paragraph(
        'We collect and store the following categories of Personal Information',
        ' from and about each user category as summarized below. Our use of the',
        ' information set forth below is described in the section “How Does',
        ' Schoolhouse Use the Information it Collects.”\n'
      ),
      ctx.paragraph(ctx.text('Account Sign-up and Profile Information\n', { bold: true })),
      ctx.bulletedList(
        ctx.listItem(
          'Information you provide when registering for an account at',
          ' Schoolhouse account at Schoolhouse, such as first and last name, email',
          ' address, zip code, country, birthdate (for compliance purposes) and',
          ' phone number (e.g, if you want to receive SMS reminders). Schoolhouse',
          ' uses a ',
          ctx.linkExternal(getAboutUrl('third-parties'), 'third party service'),
          ' to manage and store user passwords.\n'
        ),
        ctx.listItem(
          'Any additional optional profile information you may provide during',
          ' registration or related to your profile, such as a photo (or avatar),',
          ' more detailed profile information (e.g. hobbies), responses to',
          ' questions such as “how you you hear about Schoolhouse”, and other',
          ' login credentials to link your account (e.g. your Khan Academy or Art',
          ' of Problem Solving user name).'
        )
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(ctx.text('Tutor Application and Certification Information\n', { bold: true })),
      ctx.bulletedList(
        ctx.listItem(
          'Information you may provide when applying to become a certified',
          ' tutor, such as relevant employment or professional related experience.',
          ' Tutors may also provide the information in the category “Education or',
          ' other Demographic Information”.'
        ),
        ctx.listItem(
          'Videos a tutor submits of their computer screens and themselves',
          ' taking certain courses necessary to certify to become a tutor. These',
          ' videos are used to verify a tutor’s mastery of a topic, to figure out',
          ' what topics they can tutor in, and also to display on the tutor’s',
          ' portfolio (which can be shared with colleges, etc.) at the tutors’',
          ' discretion. Please note that in addition to Schoolhouse employees and',
          ' a small number of volunteer administrators, there is also a peer',
          ' review process by which other Schoolhouse tutors review each other’s',
          ' videos to assist with the certification process. The videos are',
          ' randomly assigned for peer review.'
        )
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(ctx.text('Education or other Demographic Information\n', { bold: true })),
      ctx.bulletedList(
        ctx.listItem(
          'Education information you may provide such as school name and',
          ' location; whether you were home schooled or went to a private or',
          ' public school; graduation year; grade you are in; grades or',
          ' transcripts (for tutors); and additional academic records',
          ' pertaining to tutor applications and honorary certifications (e.g.',
          ' assessments, standardized test scores). Tutors may submit their',
          ' grades or transcripts with any personal information removed.',
          ' Schoolhouse will delete grades, transcripts or other academic',
          ' records after the tutor’s certification has been established upon',
          ' request from the tutor.'
        ),
        ctx.listItem(
          'High-level details about schooling (e.g. stage of learner — K-12 vs.',
          ' university vs. adult vs. educator) that all users are required to',
          ' provide during registration.'
        ),
        ctx.listItem(
          'Demographic information you may optionally provide such as your birth',
          ' date, gender pronouns, and languages spoken.'
        )
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(ctx.text('Tutoring Session Information\n', { bold: true })),
      ctx.bulletedList(
        ctx.listItem(
          'Information collected from both learners and tutors such as tutoring',
          ' subject areas and tutoring attendance (reported from Zoom). The',
          ' attendance data is shown to both the tutor and learner (to confirm',
          ' they are following the attendance policy) and may also be shown to',
          ' parents and other designated third parties (such as coaches) with',
          ' the learner’s permission.'
        ),
        ctx.listItem(
          'Information collected when hosting a tutoring session, such as',
          ' tutoring description from the tutor.'
        ),
        ctx.listItem(
          'Zoom recordings: Schoolhouse currently uses ',
          ctx.linkExternal(
            'https://explore.zoom.us/docs/en-us/education.html',
            'Zoom for Education'
          ),
          ' to host its tutoring sessions. The tutoring sessions (including',
          ' transcripts and public Zoom chat logs) are recorded from both learners',
          ' and tutors to ensure the safety and quality of tutoring on the',
          ' Services. To learn about who has access to these recordings and for',
          ' what purposes, and how long they are kept, please see ',
          ctx.linkExternal(getArticleUrl('zoom-recording-policy'), 'this article'),
          `. We store video recordings on our video hosting service provider's`,
          ' servers. To learn more, read the ',
          ctx.linkExternal(
            'https://explore.zoom.us/en/schools-privacy-statement/',
            'Zoom Retention Policy'
          ),
          '.'
        )
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(ctx.text('Messaging and Other Communications\n', { bold: true })),
      ctx.bulletedList(
        ctx.listItem(
          `Learners' feedback to tutors on sessions: Learners may choose to`,
          ' provide tutors and Schoolhouse with feedback on their tutoring',
          ' sessions. Tutors can also choose to share positive feedback publicly',
          ' on their tutor profiles (no learner name is attached).'
        ),
        ctx.listItem(
          `Tutor's feedback to learners: Tutors may provide learners with both`,
          ' positive & constructive feedback that can further their learning. The',
          ' feedback is shared with the learner but not made public.'
        ),
        ctx.listItem(
          'Comments on tutoring sessions: Users can choose to provide comments',
          ' and communicate in advance of a tutoring session (e.g. about',
          ' logistics); the comments are publicly displayed on the session page',
          ' for all logged in users (not the general public). Users can flag and',
          ' report comments.'
        ),
        ctx.listItem(
          'Direct messages and group chats on the Services: A user may choose to',
          ' direct message other users (either another individual or in a group',
          ' associated with a tutoring series) such as for logistics corresponding',
          ' to a tutoring session. These messages are shared with the other users',
          ' in the messaging group as well as safety monitors that ensure the',
          ' communications remain safe.'
        )
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(ctx.text('Subworld Content\n', { bold: true })),
      ctx.bulletedList(
        ctx.listItem(
          'Users can choose to provide posts, comments and reactions in help and',
          ' discussion channels in designated subworlds. This content is entirely',
          ' visible to other users in the subworld and parts may also be visible',
          ' to all visitors to the website. Users can flag and report comments.'
        )
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(ctx.text('Feedback Pages\n', { bold: true })),
      ctx.bulletedList(
        ctx.listItem(
          'Feedback pages: All users with accounts (both learners and tutors)',
          ' may post to the ',
          ctx.linkExternal('https://schoolhouse.world/feedback', 'feedback page'),
          ' (text, links and images) which is hosted and stored by a ',
          ctx.linkExternal(getAboutUrl('third-parties'), 'third party service provider'),
          ' ',
          ctx.linkExternal('https://canny.io/', 'Canny.io'),
          '. This page allows users to provide feedback on the Service in a',
          ' feedback page available to all signed in users.'
        )
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(
        ctx.text('Content (photos, videos, photographs, documents or audio files)\n', {
          bold: true,
        })
      ),
      ctx.bulletedList(
        ctx.listItem(
          'Please see the “Tutor Application and Certification Information”',
          ' category, the “Feedback Pages” category, and the “Tutoring Session”',
          ' category listed above.'
        )
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(ctx.text('Geolocation\n', { bold: true })),
      ctx.bulletedList(
        ctx.listItem(
          'Schoolhouse does not collect precise geolocation information without',
          ' user permission. Schoolhouse collects non-precise geolocation',
          ' information such as ',
          ctx.linkExternal(IP_ADDRESS, 'IP address'),
          ' information and zip code.'
        )
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(ctx.text('Partnerships\n', { bold: true })),
      ctx.bulletedList(
        ctx.listItem(
          'Schoolhouse may feature a special program or activity that we offer',
          ' in partnership with a third party. If you participate in these special',
          ' programs, Schoolhouse may collect additional information from you to',
          ' facilitate those programs.'
        )
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(ctx.text('Research Studies\n', { bold: true })),
      ctx.bulletedList(
        ctx.listItem(
          'Schoolhouse may engage in research studies in partnership with third',
          ' parties and academic researchers to understand the effectiveness of',
          ' tutoring techniques and the impact of peer tutoring as a whole. If you',
          ' opt in to participating in a research project, Schoolhouse may',
          ' collect additional information from you to facilitate the research',
          ' study, which may include parental emails and consent for users under',
          ' 18. Participation in research studies is voluntary, and Schoolhouse',
          ' will provide information on data that will be collected and shared',
          ' as part of the study to users prior to the study beginning.',
          ' Participating in some programs may require participating in an',
          ' associated research study.'
        )
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(ctx.text('Survey Information\n', { bold: true })),
      ctx.bulletedList(
        ctx.listItem(
          'Your responses to surveys that we or our ',
          ctx.linkExternal(getAboutUrl('third-parties'), 'service providers'),
          ' might ask',
          ' you to complete for research purposes. These surveys may also ask for',
          ' your name and email as well as other free form and multiple choice',
          ' fields. We may also ask learners survey questions related to how the',
          ' school year has been going for them to get a sense of how learners',
          ' are doing in school and personalize the experience as needed.'
        )
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(ctx.text('Conduct and Safety Information\n', { bold: true })),
      ctx.bulletedList(
        ctx.listItem(
          'If a user reports another user on the Service (e.g. such as through a',
          ' “report abuse” button) for conduct or other safety issues, Schoolhouse',
          ' collects this information to investigate the report.'
        )
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(
        ctx.text('Contact Information and Correspondence with Schoolhouse\n', { bold: true })
      ),
      ctx.bulletedList(
        ctx.listItem(
          'As a user of the Service or Website, you may choose to provide us',
          ' with your ',
          ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
          ', such as a name, email address, and',
          ' telephone number. Some examples include when you send us an email',
          ' asking a question, communicate with customer support, participate in a',
          ' video testimonial about our Service, or choose to participate in any',
          ' research efforts with Schoolhouse to improve the Service. We collect',
          ' records and copies of your correspondence (including email address and',
          ' chat messages) when contacting us for support.'
        )
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(ctx.text('Testimonials\n', { bold: true })),
      ctx.bulletedList(
        ctx.listItem(
          'We may collect certain ',
          ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
          ' (such as your name, photo, and/or video) if you choose to give us a',
          ' testimonial. We post testimonials on our Service which may contain',
          ' this personal information in the testimonial. We obtain the',
          " individual's consent in advance to ensure we have permission to post",
          ' this content publicly. To request the removal of your personal',
          ' information from our testimonials, please contact us at',
          ' privacy@schoolhouse.world.'
        )
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(ctx.text('Donation Information\n', { bold: true })),
      ctx.bulletedList(
        ctx.listItem(
          'We may collect certain ',
          ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
          ' such as your name and donation amount) if you choose to donate to',
          ' Schoolhouse (a 501(c)(3) organization), such as through ',
          ctx.linkExternal('schoolhouse.world/donate', 'schoolhouse.world/donate'),
          '. This ',
          ctx.linkExternal('https://schoolhouse.world/donate', 'donation page'),
          ' is hosted, operated and stored by a third party service provider,',
          ' Network for Good. Schoolhouse does not collect or process any of the',
          ' payment information and does not receive any of this from Network for',
          ' Good.'
        )
      ),
      ctx.paragraph('\n'),
      ctx.headingThree('Information Collected Automatically'),
      ctx.paragraph(
        'When you use the Service or Website, we (or our ',
        ctx.linkExternal(getAboutUrl('third-parties'), 'service providers'),
        ') may use various technologies that automatically record or collect certain',
        ' information, including ',
        ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
        ', from your browser or your device about the services you use and how',
        ' you use them. This information is necessary for the adequate performance',
        ' of the contract between you and us, to enable us to comply with legal',
        ' obligations and given our legitimate interest in being able to provide',
        ' and improve the Service and Website. For example, this could include the',
        ' frequency and duration of your to Schoolhouse or the Website',
        ' (similar to TV ratings that indicate how many people watched a',
        ' particular show). If you use Schoolhouse on different devices, we may',
        ' link the information we collect from those different devices to help us',
        ' provide a consistent Service across your different devices. If we do',
        ' combine any automatically-collected information with personal',
        ' information, we will treat the combined information as personal',
        ' information, and it will be protected as per this Privacy Policy.\n'
      ),
      ctx.paragraph('The technologies and information we automatically collect include:\n'),
      ctx.bulletedList(
        ctx.listItem(
          ctx.text('Cookies and other similar technologies', { bold: true }),
          ': We (or our ',
          ctx.linkExternal(getAboutUrl('third-parties'), 'service providers'),
          ') may use ',
          ctx.linkExternal(COOKIES, 'cookies or similar technologies'),
          ' to identify your browser or device. We (or our service providers)',
          ' may also use these technologies (never in logged in areas of our',
          ' Service) in connection with advertising of our Service that may appear',
          ' on other sites or in connection with advertising their products',
          ' outside of our Service (e.g., if you view an embedded YouTube video',
          ' player on our Service, YouTube may place cookies or similar',
          ' technologies on your browser when you play the video off our site).',
          ' We don’t allow these third-parties to advertise directly on our',
          ' Service (i.e. such as when an advertiser would bid to place an',
          ' advertisement directly on a platform such as Facebook), but we may',
          ' serve contextually relevant advertising for third-party products',
          ' and services ourselves that we believe may be of interest to you',
          ' (e.g., our co-created content with Wacom). Please read our ',
          ctx.linkExternal(getAboutUrl('cookies'), 'Online Tracking Technologies Policy'),
          ' for more details, including how to modify your cookie settings and a',
          ' list of the tracking technologies we use.'
        ),
        ctx.listItem(
          ctx.linkExternal(LOCAL_STORAGE, ctx.text('Local storage', { bold: true })),
          ': We may also use, collect, and store information and preferences',
          ' locally on your device using mechanisms such as browser-based web',
          ' storage (including HTTP cookies and HTML5) and application data',
          ' caches.\n',
          'Like many services, Schoolhouse (or our service providers) uses both',
          ' local storage and cookies and other similar technologies to analyze',
          ' trends, gather demographic information about our users, understand',
          ' your engagement with the Service and Website, administer the Service',
          ' and Website, tailor the Service and Website for you, and to help the',
          ' Service and Website work better for you - for example, by remembering',
          ' your language preferences or other information so that you will not',
          ' have to re-enter it during the next time you visit the Website or',
          ' Services. Please read our ',
          ctx.linkExternal(getAboutUrl('cookies'), 'Online Tracking Technologies Policy'),
          ' for more details.'
        ),
        ctx.listItem(
          ctx.text('Device information', { bold: true }),
          ': We collect device-specific information such as your device type,',
          ' device brand, operating system, hardware version and settings,',
          ' device settings, file and software names and types, battery and',
          ' signal strength, and device identifiers. We may also collect device',
          ' event information such as crashes, system activity, browser type,',
          ' browser language, the date and time of your request and referral URL.',
          ' This helps us measure how the Website and Service is performing,',
          ' improve Schoolhouse for you on your particular device, and send you',
          ' push notifications if you’ve opted in to receive them.'
        ),
        ctx.listItem(
          ctx.text('Mobile application information', { bold: true }),
          ': Certain mobile applications or “apps” include a unique application',
          ' number. This number and information about your installation (for',
          ' example, the operating system type and application version number)',
          ' may be sent to Schoolhouse when you install or uninstall that',
          ' application, such as when you install the Schoolhouse App, when you',
          ' open the application, or when that application periodically contacts',
          ' our servers, such as for automatic updates. Additionally, we may',
          ' receive application state and crash log information which will help',
          ' us debug and improve the Schoolhouse App.'
        ),
        ctx.listItem(
          ctx.text('Server log information', { bold: true }),
          ': Like most online services, when you use our Service or Website we',
          ' automatically collect and store certain information in our ',
          ctx.linkExternal(SERVER_LOGS, 'server logs'),
          '. Examples include things like:\n- Telephone log information like',
          ' your phone number and SMS routing information\n- ',
          ctx.linkExternal(IP_ADDRESS, 'IP address'),
          '\n- Details of how you used our Service or Website, such as your',
          ' activity on the Service or Website (including product event data used',
          ' to track progress or user activity), and the frequency and duration of',
          ' your visits to the Service or Website (similar to TV ratings that',
          ' indicate how many people watched a particular show)\n',
          'This information helps us make decisions about what we should work on',
          ' next - for example, by showing which features are most popular and to',
          ' send notifications to a device (such as push notifications if the user',
          ' opted in) to encourage the user to finish an activity or start the',
          ' next activity. We may use your IP address and share it with third',
          ' parties to help diagnose problems with our servers, for security',
          ' purposes, to manage the Service or Website, to enhance the Services',
          ' or Website based on usage pattern data and, for analytics and for',
          ' advertising.'
        ),
        ctx.listItem(
          ctx.text('Location information', { bold: true }),
          ': When you use our Service or Website, we may collect and process',
          ' information about your geographic location, for example, based on your',
          ' IP address. We collect both coarse (i.e., city-level) location data',
          ' and may collect precise location data. If we do request precise',
          ' geolocation information (“precise” meaning sufficient to identify',
          ' street name and name of city or town) from you, we ask for your',
          ' explicit opt-in permission. From all users, if they upload photos or',
          " videos from their computer, or from their phone's camera roll",
          " directly, as opposed to choosing to use Schoolhouse's own",
          ' application camera, we collect what the device sends us. If the',
          ' device sends us',
          ' Exchangeable Image File Format (EXIF data) (metadata contained within',
          ' the image file - e.g. camera model, lighting conditions, and',
          ' location (if enabled)) this can contain location data (and may',
          ' contain precise location data).\n',
          'We will not store or track your precise device location on an ongoing',
          ' basis or without your permission. We do not share precise',
          ' geolocation data with third-parties, other than our ',
          ctx.linkExternal(getAboutUrl('third-parties'), 'service providers'),
          ' as necessary to provide the Service. If you no longer wish',
          ' to allow us to track your location information, you may opt-out at',
          ' any time by turning it off at the device level.'
        ),
        ctx.listItem(
          ctx.text('Cross-device collection', { bold: true }),
          ': To provide users with a seamless online experience, we may link',
          ' your identifiers on different browsers and environments you are using.',
          ' We may also work with third-party partners to employ technologies,',
          ' including the application of statistical modeling tools, which permit',
          ' us to recognize and contact you across multiple devices.'
        )
      ),
      ctx.paragraph('\n'),
      ctx.headingThree('Information Received from Third Party Sources'),
      ctx.paragraph(
        'We may also obtain information, including ',
        ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
        ', from third-party sources to update or supplement the information you',
        ' provided, or we collected automatically. This may include aggregated',
        ' anonymous information or certain personal information that may be',
        ' provided to us. If we receive personal information from third parties,',
        ' we will handle it in accordance with this Privacy Policy. If we directly',
        ' combine information we receive from other third-parties with personal',
        ' information that we collect through the Service or Website, we will',
        ' treat the combined information as personal information and handle it in',
        ' accordance with this Privacy Policy. Additionally, we may use any',
        ' aggregated anonymous information received by third-parties as set forth',
        ' below under the heading ',
        ctx.linkInternal(
          getPrivacyUrl(TAGS.WILL_SCHOOLHOUSE_SHARE_ANY),
          '“Aggregated Information and Non-Identifying Information”'
        ),
        '. Local law may require you authorize the third party to share your',
        ' information with us before we can acquire it. We do not control,',
        ' supervise, or respond to how third parties providing your information',
        ' process your personal information, and any information request',
        ' regarding the disclosure of your personal information to us should be',
        ' directed to such third parties.'
      ),
    ]),
  },
  {
    anchor: TAGS.ONLINE_ANALYTICS,
    content: composeRichText(ctx => [
      ctx.heading('Online Analytics, Advertising, and Do-Not Track'),
      ctx.paragraph(ctx.text('Online Analytics Providers\n', { bold: true })),
      ctx.paragraph(
        'We may collect analytics data, or use third-party analytics tools such',
        ' as Google Analytics, to help us measure traffic and usage trends for',
        ' the Services and Website and to understand more about the demographics',
        ` of our users. You can learn more about Google's practices at `,
        ctx.linkExternal(
          'http://www.google.com/policies/privacy/partners',
          'http://www.google.com/policies/privacy/partners'
        ),
        ', and view its currently available opt-out options at ',
        ctx.linkExternal(
          'https://tools.google.com/dlpage/gaoptout',
          'https://tools.google.com/dlpage/gaoptout.'
        ),
        ' These third parties use the sorts of technologies described in the ',
        ctx.linkInternal(
          getPrivacyUrl(TAGS.WHAT_INFORMATION_DOES_SCHOOLHOUSE_COLLECT_2),
          '“Automatically Collected Information”'
        ),
        ' section above. The information',
        ' collected by this technology will be disclosed to or collected directly',
        ' by these service providers. A current list of analytics providers that',
        ' we use is located ',
        ctx.linkInternal(getAboutUrl('third-parties'), 'here'),
        '.\n'
      ),
      ctx.paragraph(ctx.text('Advertising Technology Providers\n', { bold: true })),
      ctx.paragraph(
        'Schoolhouse does not display any ',
        ctx.linkExternal(BEHAVIORALLY_TARGETED_ADVERTISING, 'behaviorally targeted advertising'),
        ' on our Service (i.e. in logged in areas of our Website). We may serve ',
        ctx.linkExternal(CONTEXTUAL_TARGETING, 'contextually relevant advertising'),
        ' for third-party products and services that we believe may be of',
        ' interest to you (e.g. a partnership with Wacom). Additionally, as a',
        ' non-profit, we are sustained through philanthropy and may have paid',
        ' content partnerships with universities and other third parties. We will',
        ' only enter partnerships that help us support learners and tutors as part',
        ' of our educational mission. Sponsored content will always be labeled',
        ' (e.g., “Sponsored by _”). We will not share any of your personal',
        ' information with these sponsors without your explicit consent, and',
        ' these sponsors do not have the ability to track or collect data about',
        ' our site visitors or users.\n'
      ),
      ctx.paragraph(
        'However, data about your activities on our Website (outside of our',
        ' logged in Service) may be collected by us or by third-party advertising',
        ' technology providers for use in delivering online advertising tailored',
        ' to your individual characteristics, activities, interests and other',
        ' factors. These ad services may track your online activities over time',
        ' and across the web by collecting information through automated means,',
        ' including through the use of the various technologies described above,',
        ' and they may use this information, and other information they receive',
        ' from us or other sources, to deliver advertisements to you on our',
        ' website, our emails, and other websites. We and third-party vendors may',
        ' use first-party cookies and third-party cookies together, and other',
        ' automated technologies, and in some cases additional information, (i)',
        ' to inform, optimize, and serve ads based on past visits to our',
        ' Websites or other factors and (ii) to report how ad impressions, other',
        ' uses of ad services, and interactions with these ad impressions and ad',
        ' services are related to visits to our Websites or other factors.\n'
      ),
      ctx.paragraph(
        'You may visit the ',
        ctx.linkExternal(
          'https://optout.networkadvertising.org/?c=1',
          `Network Advertising Initiative's Consumer Opt-Out page`
        ),
        ' or the ',
        ctx.linkExternal(
          'https://optout.aboutads.info/?c=2&lang=EN',
          `Digital Advertising Alliance's Consumer Opt-Out page`
        ),
        ' to opt out of receiving tailored advertising based on your Internet',
        ' browsing activities from companies that participate in those programs.',
        ' You may also manage certain advertising cookies by visiting the ',
        ctx.linkExternal('https://www.youronlinechoices.eu/', 'EU-based Your Online Choices'),
        '. You may also be able to limit interest-based advertising through the',
        ' settings on your mobile device by selecting “limit ad tracking” (iOS) or',
        ' “opt-out of interest based ads” (Android). You may also be able to',
        ' opt-out of some - but not all - interest-based ads served by mobile ad',
        ' networks by visiting http://youradchoices.com/appchoices and',
        ' downloading the mobile AppChoices app. Some of our advertising-related',
        ' activities may involve the services of companies that do not',
        ' participate in those programs. For example, we may use the Google',
        ' AdWords remarketing service to advertise on third party websites',
        ' (including Google) to previous visitors to our Website. You can set',
        ' preferences for how Google advertises to you using the ',
        ctx.linkExternal('http://www.google.com/settings/ads/onweb/', 'Google Ad Preferences page'),
        '. The opt-outs apply only to the browser in which you set them. For',
        ' example, if you set the opt-out while using Firefox, but then use',
        ' Chrome, the opt-out will not be active in Chrome. To opt out in Chrome,',
        ' you will need to repeat the opt-out process.\n'
      ),
      ctx.paragraph(
        'The opt-outs described in this Privacy Policy do not block or delete',
        ' cookies, or prevent the use of cookies or other technologies for',
        ' purposes other than selecting ads based on your interests as inferred by',
        ' your online behavior. If you opt out, data may still be collected about',
        ' your web browsing activities and you will still see advertising. Ads',
        ' may be delivered based on other factors, such as the content of the',
        ' web page in which they are shown, or demographic information obtained',
        ' through other means.\n'
      ),
      ctx.paragraph(
        'Please read our ',
        ctx.linkExternal(getAboutUrl('cookies'), 'Online Tracking Technologies Policy'),
        ' for more details, including how to remove or modify your cookie',
        ' settings and a list of the advertising tracking technologies we use.\n'
      ),
      ctx.paragraph(ctx.text('YouTube\n', { bold: true })),
      ctx.paragraph(
        'Schoolhouse uses YouTube to display video content on the Website and',
        ' Services. YouTube incorporates tracking technologies, which may be',
        ' present in the YouTube videos embedded on the Service, which may',
        ' collect information from your browser when you view the video on the',
        ' Service, including device identifiers and/or cookie IDs. This',
        ' information is collected directly and automatically by YouTube and',
        ' its partners, and Schoolhouse does not participate in these data',
        ' transmissions. Schoolhouse does not provide any personal',
        ' information, such as user names, to YouTube.\n'
      ),
      ctx.paragraph(
        'Schoolhouse has taken steps to minimize this tracking activity, to the',
        " extent we have controls over a third-party's practices. We have",
        ' turned on “Privacy-Enhanced Mode” for YouTube videos displayed on the',
        " Service and Website. We also utilize Google's “child-directed",
        ' treatment” tags to alert Google that these YouTube videos may be',
        ' embedded on webpages visited by children or learners. You can learn',
        " more about Google's practices at ",
        ctx.linkExternal(
          'http://www.google.com/policies/privacy/partners',
          'http://www.google.com/policies/privacy/partners'
        ),
        '. You may also be able to control ad tracking through YouTube by',
        ' visiting the Google Ad Settings page, currently available at: ',
        ctx.linkExternal('https://adssettings.google.com', 'https://adssettings.google.com'),
        '.\n'
      ),
      ctx.paragraph(ctx.text('Do Not Track Disclosure\n', { bold: true })),
      ctx.paragraph(
        'Do Not Track ("DNT") is a privacy preference that users can set in',
        ' certain web browsers to inform websites and services that they do not',
        ' want certain information about their webpage visits collected over time',
        ' and across websites or online services. However, we do not recognize or',
        ' respond to browser-initiated DNT signals, as the Internet industry is',
        ' still working to determine what DNT means, how to comply with DNT, and',
        ' how to create a common approach to responding to DNT. For more',
        ' information on “do not track”, please visit ',
        ctx.linkExternal('https://www.allaboutdnt.com/', 'www.allaboutdnt.com'),
        '.\n'
      ),
    ]),
  },
  {
    anchor: TAGS.HOW_DOES_SCHOOLHOUSE_USE_THE_INFORMATION,
    content: composeRichText(ctx => [
      ctx.heading('How Does Schoolhouse Use the Information It Collects?'),
      ctx.paragraph(
        'First and foremost, you should know that Schoolhouse does not ',
        ctx.linkExternal(SELL, 'sell'),
        ` or rent any of your, or your child's, `,
        ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
        ' to any third-party for any purpose - including for advertising or',
        ' marketing purposes. ',
        ctx.linkExternal(THIRD_PARTY_ADVERTISING, 'Third-Party Advertising'),
        ' is not permitted on areas where users are required to log in to',
        ' Schoolhouse and personal information collected from ',
        ctx.linkExternal(LEARNER, 'learners'),
        ' is never used for ',
        ctx.linkExternal(BEHAVIORALLY_TARGETED_ADVERTISING, 'behaviorally-targeted advertising'),
        ' to learners (by us or third-parties). We may serve ',
        ctx.linkExternal(CONTEXTUAL_TARGETING, 'contextually relevant advertising'),
        ' for third-party products and services that we believe may be of',
        ' interest to you (e.g. a partnership with Wacom).\n'
      ),
      ctx.paragraph(
        'We use your personal information only when we have a valid legal basis to do',
        ' so. More specifically, this information collected is used to:\n'
      ),
      ctx.bulletedList(
        ctx.listItem(
          ctx.text('Power Our Services.', { bold: true }),
          ' This includes, operating, providing, enhancing and improving the',
          ' Service and Website, for example, by developing new products and',
          ' features, and personalizing or customizing the Service and',
          ' Website for you or providing access to restricted parts of our',
          ' Website or Services.'
        ),
        ctx.listItem(
          ctx.text('Process Your Transactions.', { bold: true }),
          ' To fulfill your requests and provide any information you request, such',
          ' as responding to your emails, submissions, questions, comments,',
          ' requests for information or customer support or for event',
          ' registration, scheduling or attendance; or for any other purpose',
          ' for which the information was collected (e.g., to process your',
          ' tutor application), or for which you provide your consent (if',
          ' required).'
        ),
        ctx.listItem(
          ctx.text('Provide Internal Analytics and Reviews.', { bold: true }),
          ' This includes using for internal audits and review, for analyzing how the',
          ' Website and Services are used, providing statistics about the',
          ' usage levels of the Website and Services, and diagnosing service',
          ' or technical problems.'
        ),
        ctx.listItem(
          ctx.text('Communicate with You.', { bold: true }),
          ' To send you information about features on our Service and Website or',
          ' changes to our policies and Service or Website or communicate with you',
          ' about your use of the Service and Website, your account, or',
          ' transactions with us. We may also send you security alerts, and',
          ' support and administrative messages and otherwise facilitate your use',
          ' of, and our administration and operation of the Service and Website',
          ' (including notifying you of data privacy incidents). Additionally, we',
          ' may provide tutors, learners, and coaches (teachers/parents)',
          ' information about events, announcements, offers, promotions,',
          ' products, including third-party products, and services we think',
          ' will be of interest to you and notify and contact contest and',
          ' sweepstakes entrants.'
        ),
        ctx.listItem(
          ctx.text('Determine Eligibility or Evaluate Your Performance.', { bold: true }),
          ' To determine eligibility for membership, credentials, designations, or',
          ' volunteer opportunities or to evaluate your performance on continued',
          ' learning courses and assist you in the tracking of your progress. For',
          ' tutor applications and tutoring sessions we may record, save, and share',
          ' video and audio recordings for review by peer reviewers, a review board,',
          ' and/or a team of internal reviewers. All such reviewers will be',
          ' restricted from using or disclosing such videos other than for the',
          ' purposes related to providing or improving the Services or our',
          ' business.'
        ),
        ctx.listItem(
          ctx.text('Request Your Participation in Surveys or Focus Groups.', { bold: true }),
          ' To request your participation in ratings, reviews, surveys, focus groups,',
          ' or other initiatives which help us to gather information used to develop',
          ' and enhance our Website, products and Services.'
        ),
        ctx.listItem(
          ctx.text('Conduct Research.', { bold: true }),
          ' Schoolhouse may engage in research studies in partnership with third',
          ' parties and academic researchers to understand the effectiveness of',
          ' tutoring techniques and the impact of peer tutoring as a whole.',
          ' Participation in research studies is voluntary, and participating in',
          ' some programs may require participating in an associated research study.'
        ),
        ctx.listItem(
          ctx.text('Security and Fraud Prevention.', { bold: true }),
          'Most crucially, to protect our community by making sure the Service and',
          ' Website remains safe and secure, such as by detecting, investigating',
          ' and preventing activities that may violate our policies or be illegal.'
        ),
        ctx.listItem(
          ctx.text('Comply with Law.', { bold: true }),
          'To comply with applicable law - for example, to satisfy tax or reporting obligations,',
          ' to comply with a lawful governmental request or notify you of data privacy incidents',
          ' when legally required.'
        )
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(
        'We use automatically collected information (described in the ',
        ctx.linkExternal(
          getPrivacyUrl(TAGS.WHAT_INFORMATION_DOES_SCHOOLHOUSE_COLLECT_2),
          '"Information collected automatically"'
        ),
        ' section above) to provide and support our Service and Website, and for',
        ' the additional uses described in that section of our Privacy Policy. To',
        ' learn more about how we use your information for personalization and',
        ' tracking, please see the ',
        ctx.linkExternal(getAboutUrl('cookies'), 'Online Tracking Technologies Policy'),
        '.\n'
      ),
      ctx.paragraph(
        'We process this information given our legitimate interest in improving',
        ' the Service and Website and our users’ experience with it, taking into',
        ' consideration your interests, rights, and expectations. Depending on the',
        ' circumstances, we may also rely on your consent or the fact that the',
        ' processing is necessary for the adequate performance of the contract',
        ' with you, to protect your vital interest or those of other persons or',
        ' the Service and Website, and to comply with applicable laws.',
        ' Additionally, we will process your personal information for the purposes',
        ' listed in this section related to marketing given our legitimate',
        ' interests in undertaking marketing activities to offer you products and',
        ' services that may be of your interest. You can opt-out of receiving',
        ' marketing communications from us by following the unsubscribe',
        ' instructions included in our marketing communications or changing your',
        ' notification settings within your Schoolhouse account.\n'
      ),
    ]),
  },
  {
    anchor: TAGS.WILL_SCHOOLHOUSE_SHARE_ANY,
    content: composeRichText(ctx => [
      ctx.heading('Will Schoolhouse Share Any Information It Collects?'),
      ctx.paragraph(
        'Schoolhouse does not ',
        ctx.linkExternal(SELL, 'sell'),
        ` or rent your (or your child's) `,
        ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
        ' to any third party for any purpose - including for advertising or',
        ' marketing purposes.\n'
      ),
      ctx.paragraph(
        'Furthermore, we do not share personal information with any third',
        ' parties except in the limited circumstances described in this Privacy',
        ' Policy and as set forth below:\n'
      ),
      ctx.bulletedList(
        ctx.listItem(
          ctx.text('Other Users You Share and Communicate with on Schoolhouse', { bold: true }),
          ': Limited profile information of users’ public profiles can be viewed',
          ' by the general public (e.g. non-logged in visitors to the Website). If',
          ' you are a ',
          ctx.linkExternal(LEARNER, 'learner'),
          ' or tutor, you may choose to share information or content through the',
          ' Service with other Schoolhouse learners and tutors — for example,',
          ' things like your account information, more detailed profile',
          ' information or other information you share with tutors and learners,',
          ' teachers you are communicating with through Schoolhouse Messaging',
          ' or our other collaboration features.\nPlease keep in mind that',
          " information (including personal Information or children's personal",
          ' information) or content that you voluntarily disclose to others -',
          ' including to other Schoolhouse users you interact with through the',
          ' Service (such as messages you might send other users or other',
          ' learners and tutors you collaborate with) - can be viewed, copied,',
          ' stored, and used by the people you share it with. We cannot control',
          ' the actions of people with whom you choose to share information and',
          ' we are not responsible for the collection, use, or disclosure of',
          ' such information or content by others.'
        ),
        ctx.listItem(
          ctx.text('Volunteers for Peer Review, Session Review and Mentoring', { bold: true }),
          ': We disclose personal information to our volunteers for peer review,',
          ' session review, and other operations of the tutoring platform that',
          ' leverage volunteers.'
        ),
        ctx.listItem(
          ctx.text('Colleges, Universities and other Educational Institutions', { bold: true }),
          ': We may disclose personal information of learners and tutors to',
          ' schools, colleges, universities, and other educational institutions',
          ' and education-related organizations for the purposes of permitting',
          ' tracking of students and applicants of such institutions, for',
          ' research, outreach, and educational purposes, such as, but not',
          ' limited to, tracking the number of sessions a learner attended.'
        ),
        ctx.listItem(
          ctx.text('Research Partners', { bold: true }),
          ': If you have opted in for a research study, Schoolhouse may securely',
          ' share data relevant to the research study with research partners,',
          ' universities and academic researchers. This data will be used solely',
          ' for the purposes of research and will be de-identified whenever',
          ' possible. Aggregate or de-identified data may be shared through',
          ' research publications or other presentations. If research results',
          ' are made public, no personally identifiable information will be',
          ' released without the user’s explicit permission. Data shared with',
          ' research partners may include Zoom audio, transcript and chat data',
          ' from tutoring sessions.'
        ),
        ctx.listItem(
          ctx.text('Third-Party Integrations on our Service', { bold: true }),
          ': When, as a learner or tutor, you use third-party apps, websites, or',
          ' other services that use, or are integrated with, our Website or',
          ' Service, they may receive information about what you post or share.',
          ' For example, when you share a tutoring session on Twitter or Facebook,',
          ' these services receive the information that you share through this',
          ' functionality and information that you are sharing from Schoolhouse.',
          ' Please see ',
          ctx.linkInternal(getPrivacyUrl(TAGS.THIRD_PARTY_SERVICES), 'here'),
          ' for more information on third-party services. Please see here for a',
          ' list of third-party app integrations on our Service and the Website.'
        ),
        ctx.listItem(
          ctx.text('Service Providers', { bold: true }),
          ': We do work with vendors, service providers, and other partners to',
          ' help us provide the Service and Website by performing tasks on our',
          ' behalf. These service providers may be located inside or outside of',
          ' the European Economic Area (“EEA”). We may need to share or provide',
          ' information (including personal information) to them to help them',
          ' perform these business functions, for example sending emails on our',
          ' behalf, database management services, database hosting, video',
          ' hosting and storage, providing customer support software, and',
          ' security. We may also authorize these third-party companies to',
          ' collect information on our behalf. These providers have limited',
          ' access to your personal information to perform these tasks on our',
          ' behalf and are contractually bound to protect and use it only for',
          ' the purpose for which it was disclosed. Additionally, these',
          ' partners and service providers must adhere to confidentiality and',
          ' security obligations in a way that is consistent with this Privacy',
          ' Policy. Please see ',
          ctx.linkExternal(getAboutUrl('third-parties'), 'here'),
          ' for a list of the third-parties we work with to provide the Service',
          ' and the Website.'
        ),
        ctx.listItem(
          ctx.text('Social Media Platforms', { bold: true }),
          ': Where permissible according to applicable law, we may share certain',
          ' limited personal information with social media platforms (such as',
          ' Google or Facebook) and other websites, applications or partners, to',
          ' generate leads, drive traffic to our websites or otherwise market and',
          ' advertise our products or Services on those websites or applications',
          ' (this will never include personal information of learners). These',
          ' processing activities are based on our legitimate interest in',
          ' undertaking marketing activities to offer you products and services',
          ' that may be of your interest. These social media platforms with',
          ' which we may share your personal information are not controlled or',
          ' supervised by Schoolhouse. Therefore, any questions regarding how your',
          ' social media platform processes your personal information should be',
          ' directed to such provider.'
        ),
        ctx.listItem(
          ctx.text('Analytics Services', { bold: true }),
          ': We use analytics services, including mobile analytics software, to',
          ' help us understand and improve how the Service and Website is being',
          ' used. These services may collect, store and use information in order',
          ' to help us understand things like how often you use the Service or',
          ' Website, the events that occur within the application, usage,',
          ' performance data, and from where the application was downloaded. A',
          ' current list of analytics providers that we use is located ',
          ctx.linkExternal(getAboutUrl('third-parties'), 'here'),
          '.'
        ),
        ctx.listItem(
          ctx.text('Aggregated Information and Non-Identifying Information', { bold: true }),
          ': We may share aggregated information (information about our users',
          ' that we combine together so that it no longer identifies or references',
          ' an individual user) and other ',
          ctx.linkExternal(DEIDENTIFIED, 'de-identified'),
          ' or non-personally',
          ' identifiable information (such as statistics about visitors, traffic',
          ' and usage patterns) with third parties for any purpose, including',
          ' with users, partners or the press in order to, for example, demonstrate',
          ' how the Service or Website is used, spot industry trends, or to provide',
          ' marketing materials for Schoolhouse. Any aggregated information and',
          ' non-personalized information shared this way will not contain any',
          ' personal information.'
        ),
        ctx.listItem(
          ctx.text('Legal Requirements', { bold: true }),
          ': We may disclose information, including personal information, if we',
          ' have a good faith belief that doing so is necessary to comply with the',
          ' law, such as complying with a subpoena or other legal process. We may',
          ' also need to disclose personal information where, in good faith, we',
          ' think it is necessary to protect the rights, property, or safety of',
          ' Schoolhouse, our employees, our community, or others, or to prevent',
          ' violations of our ',
          ctx.linkExternal(getAboutUrl('terms'), 'Terms of Service'),
          ' or other agreements. This includes,',
          ' without limitation, responding to law enforcement and government',
          ' requests and investigating and defending ourselves against third-party',
          ' claims and allegations. Where appropriate, we may notify users about',
          ' the legal requests, unless (i) providing notice is prohibited by the',
          ' legal process itself, by court order we receive, or by applicable law;',
          ' (ii) we believe that providing notice would be futile, ineffective,',
          ' create a risk of injury or bodily harm to an individual or group, or',
          ' create or increase a risk of fraud upon Schoolhouse, or its users, the',
          ' Service or Website. In instances where we comply with legal requests',
          ' without notice for these reasons, we will attempt to notify that user',
          ' about the request after the fact where appropriate and where we',
          ' determine in good faith that we are no longer legally prevented from',
          ' doing so.'
        ),
        ctx.listItem(
          ctx.text('Fraud and Security Protection', { bold: true }),
          ': We may share information to assist with security operations for',
          ' scheduled penetration testing, auditing, and continuous improvement of',
          ' our security practices, including without limitation, exchanging',
          ' information with other companies and organizations for fraud',
          ' protection. Authorized parties of the information would comply with',
          ' our current privacy and security policies.'
        ),
        ctx.listItem(
          ctx.text('Sharing with Schoolhouse Companies', { bold: true }),
          ': Over time, Schoolhouse may grow and reorganize. We may share your',
          ' personal information with affiliates such as a parent company,',
          ' subsidiaries, joint venture partners, or other companies that we',
          ' control or that are under common control with us, in which case we',
          ' will require those companies to agree to use your personal',
          ' information in a way that is consistent with this Privacy Policy.'
        ),
        ctx.listItem(
          ctx.text('Change of control', { bold: true }),
          ': The ',
          ctx.linkExternal('https://studentprivacypledge.org/', 'Student Privacy Pledge'),
          ' requires us (and all pledgers) to commit',
          ' to how we handle data in the event of a change to our organizations',
          ' such that all or a portion of Schoolhouse or its assets are acquired',
          ' by or merged with a third-party, or in any other situation where ',
          ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
          ' that we have collected from users would be one',
          ' of the assets transferred to or acquired by that third-party.',
          ' Consistent with the Student Privacy Pledge and applicable state and',
          ' federal laws, in connection with such a change to our organization,',
          ' if any, this Privacy Policy will continue to apply to your',
          ' information, and any acquirer would only be able to handle your',
          ' personal information as per this policy (unless you give consent to',
          ' a new policy). We will provide you with notice of an acquisition',
          ' within thirty (30) days following the completion of such a',
          ' transaction, by posting on our homepage, or by email to your email',
          ' address that you provided to us. If you do not consent to the use',
          ' of your personal information by such a successor company, subject',
          ' to applicable law, you may request its deletion from the company.',
          ' We may also make information, including personal information,',
          ' available to a potential investor or purchaser (or their advisors)',
          ' in advance of any such transaction completion. In the',
          ' unlikely event that Schoolhouse goes out of business, or files for',
          ' bankruptcy, we will protect your personal information, and will not ',
          ctx.linkExternal(SELL, 'sell'),
          ' it to any third-party. For more information on our practices',
          ' regarding your data if we go out of business, please see our ',
          ctx.linkExternal(getUrl('trust-safety-privacy'), 'FAQ'),
          '.'
        ),
        ctx.listItem(
          ctx.text('Artificial Intelligence (AI) Providers', { bold: true }),
          ': Schoolhouse uses Artificial Intelligence (AI) in partnership with',
          ' third-party AI providers in several places to support learners and',
          ' tutors and to help us moderate unsafe content on our Services.',
          ' Whenever AI is used, it is for the benefit of learners and tutors,',
          ' and we do not share personally identifiable information from your',
          ' Schoolhouse account with AI providers. The AI providers do not',
          ' train their models on the data. For more details on our use of AI,',
          ' see this ',
          ctx.linkExternal(getArticleUrl('how-does-schoolhouse-use-ai'), 'FAQ Article'),
          '.'
        ),
        ctx.listItem(
          ctx.text('With your consent', { bold: true }),
          ': Other than the cases above, we won’t disclose your ',
          ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
          ' for any purpose unless you consent to it.'
        )
      ),
      ctx.paragraph('\n'),
    ]),
  },
  {
    anchor: TAGS.HOW_DOES_SCHOOLHOUSE_PROTECT,
    content: composeRichText(ctx => [
      ctx.heading('How Does Schoolhouse Protect and Secure My Information?'),
      ctx.paragraph(
        'The safety and security of your information also depends on you. You',
        ' should maintain good internet security practices. Where you have',
        ' password-protected access to an account or certain parts of the Website',
        ' or Services, you are responsible for keeping this password secure at',
        ' all times. You should not share your password with anyone. You must',
        ' prevent unauthorized access to your account and ',
        ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
        ' by selecting and protecting your password appropriately and',
        ' limiting access to your computer or mobile device and browser by',
        ' signing off after you have finished accessing your account. If you',
        ' think that any of your accounts have been compromised you should',
        ' change your account credentials with us, and in particular make',
        ' sure any compromised account does not allow access to your account',
        ' with us. We will never email you to ask for your password or other',
        ' account login information. If you receive such an email, please',
        ' send it to us so we can investigate.\n'
      ),
      ctx.bulletedList(
        ctx.listItem(
          'The security of your ',
          ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
          ' is important to us. We work hard to protect our community, and we',
          ' maintain administrative, technical, and physical safeguards, including',
          ' encryption, designed to protect against unauthorized use, disclosure',
          ' of, or access to personal information. We also operate a ',
          `'bug bounty' security program to encourage an active community of`,
          ' third-party security researchers to report any security bugs to us.',
          ' More information on this is available by contacting us at',
          ' security@schoolhouse.world.'
        )
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(
        'Although we make concerted good faith efforts to maintain the security',
        ' of personal information, and we work hard to ensure the integrity and',
        ' security of our systems, no practices are 100% immune, and we can’t',
        ' guarantee the security of information. Outages, attacks, human error,',
        ' system failure, unauthorized use, or other factors may compromise the',
        ' security of user information at any time. If you have reason to',
        ' believe that your interaction with us is no longer secure, please',
        ' immediately notify us in accordance with the “Contacting Schoolhouse”',
        ' section below.\n'
      ),
    ]),
  },
  {
    anchor: TAGS.HOW_LONG_DOES_SCHOOLHOUSE_KEEP,
    content: composeRichText(ctx => [
      ctx.heading('How Long Does Schoolhouse Keep Information About Me?'),
      ctx.paragraph(
        'We store your ',
        ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
        ' for as long as it is necessary to provide the Service or Website to',
        ' you and others, including those described above. We determine the',
        ' appropriate retention period for personal information on the basis of',
        ' the amount, nature and sensitivity of the personal information being',
        ' processed, the potential risk of harm from unauthorized use or',
        ' disclosure of the personal information, whether we can achieve the',
        ' purposes of the processing through other means, and on the basis of',
        ' applicable legal requirements (such as applicable statutes of',
        ' limitations). The time periods for which we retain your personal',
        ' information depend on the purposes for which we use it. Personal',
        ' information associated with your account will be kept until your account',
        ' is deleted, unless we no longer need the data to provide the Website,',
        ' products and Services, in which case we will delete prior to you',
        ' deleting your account.\n'
      ),
      ctx.paragraph(
        'You can, of course, request to ',
        ctx.linkInternal(
          getPrivacyUrl(TAGS.SCHOOLHOUSES_COMMITMENT_TO_PROVIDING_DELETE),
          'delete your account'
        ),
        ' at any time. Please note that we may have to retain some information',
        ' after your account is deleted, to comply with legal obligations, to',
        ' protect the safety and security of our community or our Website or',
        ' Service, or to prevent abuse of our Terms.\n'
      ),
      ctx.paragraph(
        'Even if you delete your account, keep in mind that the deletion by our ',
        ctx.linkExternal(getAboutUrl('third-parties'), 'service providers'),
        ' may not be immediate and that the deleted information may persist in',
        ' backup copies for a reasonable period of time.\n'
      ),
      ctx.paragraph(
        'We may retain ',
        ctx.linkExternal(DEIDENTIFIED, 'de-identified'),
        ' information for as long as we deem appropriate.\n'
      ),
    ]),
  },
  {
    anchor: TAGS.SCHOOLHOUSES_COMMITMENT_TO_PROVIDING,
    content: composeRichText(ctx => [
      ctx.heading(`Schoolhouse's Commitments to Providing Transparency and Your Rights`),
      ctx.paragraph(
        'We try to be transparent about what information we collect so that you',
        ' can make meaningful choices about how it is used. You may have certain',
        ' rights relating to your ',
        ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
        ', subject to local data protection laws. Please review local data',
        ' protection laws for more information. Depending on the applicable local',
        ' laws these rights may include the rights set forth in this Section. You',
        ' may also have a right not to be discriminated against for exercising',
        ' any of your rights set forth in this Section.\n'
      ),
      ctx.bulletedList(
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.SCHOOLHOUSES_COMMITMENT_TO_PROVIDING_ACCESS),
            'Access or Correct Your Personal Information'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.SCHOOLHOUSES_COMMITMENT_TO_PROVIDING_DELETE),
            'Delete Your Personal Information or Account from Schoolhouse'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.SCHOOLHOUSES_COMMITMENT_TO_PROVIDING_OBJECT),
            'Object or Restrict Processing'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(
            getPrivacyUrl(TAGS.SCHOOLHOUSES_COMMITMENT_TO_PROVIDING_OBJECT),
            'Withdraw Consent'
          )
        ),
        ctx.listItem(
          ctx.linkInternal(getAboutUrl('privacy'), 'Take Your Information Out of Schoolhouse')
        )
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(ctx.text('How to Exercise Your Rights\n', { bold: true })),
      ctx.paragraph(
        'You may request to exercise any of these rights described in this',
        ' Section by sending an email to privacy@schoolhouse.world from the same',
        ' email used to create your Schoolhouse account or as otherwise set forth',
        ' in the ',
        ctx.linkInternal(getPrivacyUrl(TAGS.CONTACTING_SCHOOLHOUSE), '"Contacting Schoolhouse"'),
        ' section below. We will respond to your request consistent with',
        ' applicable law. Please note that we may ask you to verify your identity',
        ' before taking further action on your request, for example by requiring',
        ' you to provide acceptable forms of personal identification as set forth ',
        ctx.linkInternal(getAboutUrl('privacy'), 'here'),
        '. Your personal information may be processed in responding to these',
        ' rights. We try to respond to all legitimate requests within one month',
        ' unless otherwise required by law, and will contact you if we need',
        ' additional information from you in order to honor your request or verify',
        ' your identity. Occasionally it may take us longer than a month, taking',
        ' into account the complexity and number of requests we receive. If you',
        ' are an employee of a Schoolhouse customer, we recommend you contact',
        ` your employer's system administrator for assistance in correcting or`,
        ' updating your information.\n'
      ),
      ctx.paragraph(
        'Where permitted under applicable law, we may reject requests for',
        ' access, change, or deletion that are unreasonably repetitive (more than',
        ' two times per year for access rights), risk the privacy of others,',
        ' require disproportionate technical effort (for example, developing a',
        ' new system or fundamentally changing an existing practice), or would be',
        ' extremely impractical (for instance, requests concerning information',
        ' residing on backup systems).\n'
      ),
      ctx.paragraph(ctx.text('Access or Correct Your Personal Information\n', { bold: true })),
      ctx.paragraph(
        'Schoolhouse aims to provide you with easy access to any ',
        ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
        ' we have collected about you and give you easy ways to update it or',
        ' delete it, unless we have to keep that information for legitimate',
        ' business purposes (e.g., we need at least an email address for your',
        ' account if you maintain an account with us) or legal purposes. You may',
        ' have the right to correct inaccurate or incomplete personal',
        ' information concerning you (and which you cannot update yourself',
        ' within your Schoolhouse account).\n'
      ),
      ctx.paragraph(
        ctx.text('Managing Your Information', { italic: true }),
        ': If you have registered for an account on Schoolhouse, you may update,',
        ' correct, or delete some of your profile information or your preferences',
        ' at any time by logging into your account on Schoolhouse and accessing',
        ' your account settings page. You may have to verify your identity',
        ' before you can do that. You may also, at any time, request to update',
        ' or correct, certain personal information that you have provided to us',
        ' by contacting us at privacy@schoolhouse.world from the email used to',
        ' create your Schoolhouse account once you are signed into your',
        ' Schoolhouse account.\n'
      ),
      ctx.paragraph(
        ctx.text('Accessing Your Information', { italic: true }),
        ': Upon request, Schoolhouse will provide you with information about whether we',
        ' hold any of your personal information, and, if you are a user of Schoolhouse,',
        ' you may request access to all your personal information we have on file by',
        ' contacting us as set forth above. In some cases, we won’t be able to guarantee',
        ' complete access due to legal restrictions - for example, you will not be allowed',
        ' to access files that contain information about other users or information that',
        ' is confidential to us. Where we can provide information access and correction,',
        ' we will do so for free, except where it would require a disproportionate',
        ' technical effort.\n'
      ),
      ctx.paragraph(
        ctx.text('Delete Your Personal Information or Account from Schoolhouse\n', { bold: true })
      ),
      ctx.paragraph(
        'If for some reason you ever want to delete your account (or your',
        ` child's account, if you are his or her parent), you can request to do so`,
        ' at any time by contacting us as set forth ',
        ctx.linkInternal(
          getPrivacyUrl(TAGS.SCHOOLHOUSES_COMMITMENT_TO_PROVIDING_EXERCISE),
          'above'
        ),
        '. If you are a learner, tutor, or coach (parent/teacher) using the',
        ' Service, you may also delete your account by logging into your account',
        ' and accessing your account settings page.\n'
      ),
      ctx.paragraph(
        'When you choose to delete your account, we will (subject to requirement',
        ' under applicable law) delete your profile information and any other',
        ' content you provide in your profile (such as your name, username,',
        ' password, email address, and profile photos) and depending on the',
        ' category of user you are (i.e. learner, tutor, or coach',
        ' (parent/teacher), the additional content or personal information',
        ' uploaded for use , as set forth in our ',
        ctx.linkExternal(getArticleUrl('when-i-delete-my-account'), 'FAQ'),
        '. Information that you have shared with others, others have shared about',
        ' you, or content other users may have copied and stored, is not part of',
        ' your account and may not be deleted when you delete your account.\n'
      ),
      ctx.paragraph(
        'We aim to maintain our Services and Website in a manner that protects',
        ' information from accidental or malicious destruction. Because of this,',
        ' even after you update or delete personal information you have provided',
        ' us from our Service, your personal information may be retained in our',
        ' backup files and archives for a reasonable period of time, consistent',
        ' with applicable law, as necessary for our legitimate business',
        ' interests, such as fraud detection and prevention and enhancing safety.',
        ' For example, if we suspend a Schoolhouse account for fraud or safety',
        ' reasons, we may retain certain information from that account to prevent',
        ' that user from opening a new account in the future. Also, we may retain',
        ' and use your personal information to the extent necessary to comply with',
        ' our legal obligations.\n'
      ),
      ctx.paragraph(ctx.text('Object, Restrict, or Withdraw Consent\n', { bold: true })),
      ctx.paragraph(
        'Where you have provided your consent to the processing of your personal',
        ' information by Schoolhouse you may request to withdraw your consent at',
        ' any time by changing your account settings or by sending a communication',
        ' to Schoolhouse specifying which consent you are withdrawing. Please note',
        ' that the withdrawal of your consent does not affect the lawfulness of',
        ' any processing activities based on such consent before its withdrawal.',
        ' Schoolhouse provides users with the opportunity to withdraw consent or',
        ` 'opt-out' of receiving any future marketing communications from`,
        ' Schoolhouse and its partners at any time. Please see the ',
        ctx.linkInternal(
          getPrivacyUrl(TAGS.WHAT_COMMUNICATIONS_WILL_I_RECEIVE),
          '"What Communications will I Receive from Schoolhouse?"'
        ),
        ' section below for more information. Additionally, you can always',
        ' decline to share personal information with us, or even block all',
        ` cookies. However, it's important to remember that many of`,
        ` Schoolhouse's features may not be accessible, or may not function`,
        ' properly - for example, we may not be able to remember your language',
        ' preferences for you.\n'
      ),
      ctx.paragraph(
        'In some jurisdictions, applicable law may entitle you to require',
        ' Schoolhouse not to process your personal information for certain',
        ' specific purposes (including profiling) where such processing is based',
        ' on legitimate interest. If you object to such processing Schoolhouse',
        ' will no longer process your personal information for these purposes',
        ' unless we can demonstrate compelling legitimate grounds for such',
        ' processing or such processing is required for the establishment,',
        ' exercise or defence of legal claims. Additionally, in some',
        ' jurisdictions, applicable law may give you the right to limit the ways',
        ' in which we use your personal information, in particular where (i) you',
        ' contest the accuracy of your personal information; (ii) the processing',
        ' is unlawful and you oppose the erasure of your personal information;',
        ' (iii) we no longer need your personal information for the purposes of',
        ' the processing, but you require the information for the establishment,',
        ' exercise or defence of legal claims; or (iv) you have objected to the',
        ' processing as set forth above and pending the verification whether the',
        ' legitimate grounds of Schoolhouse override your own.\n'
      ),
    ]),
  },
  {
    anchor: TAGS.ADDITIONAL_INFORMATION_OR_ASSISTANCE,
    content: composeRichText(ctx => [
      ctx.heading('Additional Information or Assistance And Article 27 GDPR Local Representative'),
      ctx.paragraph(
        'If you are located in the European Union or the EEA, you have a right',
        ' to lodge complaints about the data processing activities carried about',
        ' by Schoolhouse with the supervisory authority in your country.\n'
      ),
      ctx.paragraph(ctx.text('EU Representative\n', { bold: true })),
      ctx.paragraph(
        'Pursuant to Article 27 of the General Data Protection Regulation',
        ' (GDPR), Schoolhouse.world has ',
        ctx.linkExternal(
          'https://www.smartcertificate.com/SmartCertificate/?1%7cb9e5536e-7a21-40a5-9ee4-6b85684d43ed%7cb34fb215-5f59-419f-9fa4-3be1b158cbf9',
          'appointed'
        ),
        ' European Data Protection Office (EDPO) as its GDPR Representative in',
        ' the EU. You can contact EDPO regarding matters pertaining to the GDPR:\n'
      ),
      ctx.bulletedList(
        ctx.listItem(
          `by using EDPO's online request form: `,
          ctx.linkExternal(
            'https://edpo.com/gdpr-data-request',
            'https://edpo.com/gdpr-data-request'
          )
        ),
        ctx.listItem('by writing to EDPO at Avenue Huart Hamoir 71, 1030 Brussels, Belgium')
      ),
      ctx.paragraph('\n'),
      ctx.paragraph(ctx.text('UK Representative\n', { bold: true })),
      ctx.paragraph(
        'Pursuant to Article 27 of the UK GDPR, Schoolhouse.world has ',
        ctx.linkExternal(
          'https://www.smartcertificate.com/SmartCertificate/?1%7cbbad1e93-0a39-4e58-a422-03687e120e50%7cb34fb215-5f59-419f-9fa4-3be1b158cbf9',
          'appointed'
        ),
        ' EDPO UK Ltd as its UK GDPR representative in the UK. You can contact',
        ' EDPO UK regarding matters pertaining to the UK GDPR:\n'
      ),
      ctx.bulletedList(
        ctx.listItem(
          `by using EDPO's online request form: `,
          ctx.linkExternal(
            'https://edpo.com/uk-gdpr-data-request',
            'https://edpo.com/uk-gdpr-data-request'
          )
        ),
        ctx.listItem(
          'by writing to EDPO UK at 8 Northumberland Avenue, London WC2N 5BY, United Kingdom'
        )
      ),
      ctx.paragraph('\n'),
    ]),
  },
  {
    anchor: TAGS.WHAT_COMMUNICATIONS_WILL_I_RECEIVE,
    content: composeRichText(ctx => [
      ctx.heading('What Communications Will I Receive from Schoolhouse?'),
      ctx.paragraph(
        'If you registered on Schoolhouse, provided an email address or phone',
        ' number to us, or otherwise opted-in to receive communications from us,',
        ' you consent to us sending you messages and updates regarding your',
        ' account, the Website and Services, including privacy and security',
        ' notices, updates regarding the Website and Service, and information',
        ' regarding products, features or services from Schoolhouse (or',
        ' third-parties we believe you may be interested in). These communications',
        ' may include, but are not limited to, social media updates, SMS/MMS',
        ' messages, push and in-app notifications, email, and postal mail. If you',
        ' have an account with us, we’ll also use your email address to contact',
        ' you for customer service purposes, or for any legal matters that arise',
        ' in the course of business. We may receive a confirmation when you open',
        ' an email from us if your device supports it. We use this confirmation to',
        ' help us understand which emails are most interesting and helpful.\n'
      ),
      ctx.paragraph(
        'If you invite another person to join you on Schoolhouse by providing',
        ' their email address or phone number, we may contact them regarding the',
        ' Service using the appropriate form of communication. If they would',
        ' prefer not to receive our communications, they may opt-out using the',
        ' “Unsubscribe” or “STOP” instructions contained in those communications.\n'
      ),
      ctx.paragraph(
        'You can always unsubscribe from receiving any of our 1) marketing',
        ' emails or other marketing communications whenever you’d like by',
        ' clicking the “Unsubscribe” link at the bottom of the email; 2)',
        ' marketing SMS texts by replying or texting ‘STOP’; or 3)',
        ' marketing push notifications by turning off push notifications on',
        ' your device. You can further indicate your preferences by',
        ' contacting us using the information in the ',
        ctx.linkInternal(getPrivacyUrl(TAGS.CONTACTING_SCHOOLHOUSE), '"Contacting Schoolhouse"'),
        ' section below. Please note that opting out of marketing communications',
        ' does not opt you out of receiving important business communications',
        ' related to your current relationship with us, such as communications',
        ' about your subscriptions or event registrations, service announcements',
        ' or security information.\n'
      ),
    ]),
  },
  {
    anchor: TAGS.THIRD_PARTY_AUTHENTICATION_SERVICES,
    content: composeRichText(ctx => [
      ctx.heading('Third-party Authentication Services'),
      ctx.paragraph(
        'If you decide to register for a Schoolhouse account through an',
        ' authentication service, such as Auth0, Google Login or Clever',
        ' ("Authentication Service"), Schoolhouse may collect ',
        ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
        ' that is already associated with your account connected to the',
        ' Authentication Service. If you choose to provide such information during',
        ' registration, you are giving Schoolhouse the permission to store and use',
        ' such information already associated with your Authentication Service in',
        ' a manner consistent with this Privacy Policy. The current list of',
        ' Authentication Services that we use is listed ',
        ctx.linkExternal(getAboutUrl('third-parties'), 'here'),
        '. The Authentication',
        ' Service may collect certain other information from you in your use of',
        ' that particular service (such as G-Suite for Education). You may revoke',
        " Schoolhouse's access to your account on any Authentication Service at",
        ' any time by updating the appropriate settings in the account preferences',
        ' of the respective Authentication Service. You should check your privacy',
        ' settings on each Authentication Service to understand and change the',
        ' information sent to us through each Authentication Service. Please',
        ` review each Authentication Service's terms of use and privacy policies`,
        ' carefully before using their services and connecting to our Service.',
        ' Your use of Authentication Service is subject to the applicable',
        ' third-party terms and privacy policies. Please see ',
        ctx.linkExternal(getAboutUrl('third-parties'), 'here'),
        ' for more information on what we collect through the use of',
        ' Authentication Services and how we use that information.\n'
      ),
    ]),
  },
  {
    anchor: TAGS.THIRD_PARTY_SERVICES,
    content: composeRichText(ctx => [
      ctx.heading('Third-Party Services'),
      ctx.paragraph(
        'The Service and Website may contain links to websites, applications,',
        ' services, or other content provided by third-parties (e.g., video',
        ' players). Your use of these features may result in the collection,',
        ' processing and sharing of information about you, depending on the',
        ' features. Any information, including ',
        ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
        ', you choose to provide or that is collected by these third-parties is',
        ` subject to that third-party's policies governing privacy and`,
        ' security and is not covered by our Privacy Policy.\n'
      ),
      ctx.paragraph(
        'These other websites may place their own cookies or other files on your',
        ' computer, collect data or solicit personal information from you,',
        ' including if you view a video through an embedded video player which',
        ' is played off our Service or Website, but may appear to still be',
        ' playing on our Service or Website. The fact that we link to a',
        ' website or other third-party content is not an endorsement,',
        ' authorization or representation that we are affiliated with that',
        ' third-party, nor is it an endorsement of their privacy or',
        ' information security policies or practices. We are not responsible',
        ' for the content, features, privacy and security practices and',
        ' policies of any third-parties. We encourage you to learn about',
        ' third-parties’ privacy and security policies before providing them',
        ' with personal information.\n'
      ),
    ]),
  },
  {
    anchor: TAGS.WHAT_IF_I_AM_NOT_IN_THE_US,
    content: composeRichText(ctx => [
      ctx.heading('What if I am not in the United States?'),
      ctx.paragraph(
        'Schoolhouse is hosted and operated in the United States. If you use the',
        ' Service or Website from the European Union or the United Kingdom, or any',
        ' other region with laws governing data collection, protection and use',
        ' that may differ from United States law, please note that you may be',
        ' transferring your personal information outside of those jurisdictions to',
        ' the United States. The personal information we or our service providers',
        ' collect may be stored and processed in servers within or outside of the',
        ' United States and wherever we and our service providers have facilities',
        ' around the globe, and certain information may be accessible by persons',
        ' or companies outside of the United States who provide services for',
        ' us.\n'
      ),
      ctx.paragraph(
        'Where we transfer, store and process your ',
        ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
        ' outside of the United Kingdom, European Union, EEA, or Switzerland we',
        ' have ensured that appropriate safeguards are in place to ensure an',
        ' adequate level of protection for the rights of data subjects based on',
        ` the adequacy of the receiving country's data protection laws, for`,
        ' instance by entering into standard contractual clauses (where we',
        ' process data as a Processor for schools) or an alternative mechanism',
        ' for the transfer of data as approved by the European Commission (Art.',
        ' 46 GDPR) or other applicable regulator. For more information on these',
        ' safeguards, please see our ',
        ctx.linkExternal(getUrl('trust-safety-privacy'), 'FAQ'),
        '.\n'
      ),
      ctx.paragraph(
        'By using the Service, you consent to the transfer of your personal',
        ' information outside your home jurisdiction, including to the United',
        ' States, and to the storage of your personal information in the United',
        ' States, for the purpose of hosting, using and processing the personal',
        ' information in accordance with this Privacy Policy. You further',
        ' acknowledge that these countries may not have the same data protection',
        ' laws as the country from which you provided your personal information,',
        ' and that Schoolhouse may be subject to access requests and be compelled',
        ' to disclose your personal information to government authorities, courts',
        ' and law enforcement officials in countries (including the United States)',
        ' outside of your home country. You have the right to withdraw your',
        ' consent at any time by contacting us as described in the ',
        ctx.linkInternal(getPrivacyUrl(TAGS.CONTACTING_SCHOOLHOUSE), '"Contacting Schoolhouse"'),
        ' section below.\n'
      ),
    ]),
  },
  {
    anchor: TAGS.IS_SCHOOLHOUSE_A_CONTROLLER,
    content: composeRichText(ctx => [
      ctx.heading('Is Schoolhouse a Controller?'),
      ctx.paragraph(
        'Schoolhouse processes personal information both as a Processor and as a',
        ' Controller, as defined in the GDPR:\n'
      ),
      ctx.paragraph(
        'When teachers, school leaders, and parents enter information directly',
        ' into our Service or Website (such as registering for an account), or',
        ' when tutors, learners or parents use our Services or the Website outside',
        ' of the school context, Schoolhouse will generally be the Controller for',
        ' the user data, as outlined above in ',
        ctx.linkInternal(
          getPrivacyUrl(TAGS.WHAT_INFORMATION_DOES_SCHOOLHOUSE_COLLECT),
          '"What information does Schoolhouse Collect?"'
        ),
        ' section. However, in certain circumstances, where requested by the',
        ` student's school, Schoolhouse will be the Processor (or service provider`,
        ' as defined under CCPA), including of Student Data, and will process such',
        ' data only at the direction and control of the school. Additionally,',
        " Schoolhouse will only delete records per the school's specific",
        ' instructions and will request that you make any requests for access,',
        ' correction or deletion of personal information through the school.',
        ' Schoolhouse will respond to such requests when received from the school.',
        ` Please see Schoolhouse's policy on `,
        ctx.linkInternal(
          getPrivacyUrl(TAGS.HOW_LONG_DOES_SCHOOLHOUSE_KEEP),
          'data retention and deletion'
        ),
        '.\n'
      ),
    ]),
  },
  {
    anchor: TAGS.CALIFORNIA_PRIVACY_DISCLOSURES,
    content: composeRichText(ctx => [
      ctx.heading('California Privacy Disclosures'),
      ctx.paragraph(ctx.text('Do Not Track\n', { bold: true })),
      ctx.paragraph(
        'Do Not Track ("DNT") is a privacy preference that users can set in certain web browsers to inform websites and services that they do not want certain information about their webpage visits collected over time and across websites or online services. However, we do not recognize or respond to browser-initiated DNT signals, as the Internet industry is still working to determine what DNT means, how to comply with DNT, and how to create a common approach to responding to DNT. For more information on "do not track," please visit ',
        ctx.linkExternal('https://allaboutdnt.com/', 'www.allaboutdnt.com'),
        '.\n'
      ),
      ctx.paragraph(ctx.text('Notice for Minors (Users under 18)\n', { bold: true })),
      ctx.paragraph(
        'If you are under the age of 18, or the parent of a child using',
        ' Schoolhouse under the age of 18 residing in California, you are entitled',
        ' to request removal of content or information you (the minor) have',
        ' publicly posted on our Service. Minors can share content or comment on',
        ' the Service (such as commenting below tutoring sessions), which can only',
        ' be seen by users and certain limited information with the general',
        ' public. If you are a minor and would like to delete personal information',
        ' associated with your account or content you uploaded through your',
        ' account, please follow the directions set forth in the ',
        ctx.linkInternal(
          getPrivacyUrl(TAGS.SCHOOLHOUSES_COMMITMENT_TO_PROVIDING),
          `"Schoolhouse's commitments to providing Transparency and Your Rights"`
        ),
        ' section. Although we offer deletion capabilities on our Service, you',
        ' should be aware that the removal of content may not ensure complete or',
        ' comprehensive removal of that content or information posted through the',
        ' Service, as there may be ',
        ctx.linkExternal(DEIDENTIFIED, 'de-identified'),
        ' or recoverable elements of your content or information on our servers in',
        ' some form. Additionally, we will not remove content or information that',
        ' we may be required to retain at the direction of the school or under',
        ' applicable laws.\n'
      ),
      ctx.paragraph(ctx.text('“Shine the Light” Law\n', { bold: true })),
      ctx.paragraph(
        'Pursuant to California Civil Code Section 1798.83(c)(2), Schoolhouse does not share ',
        ctx.linkExternal(PERSONAL_INFORMATION, 'personal information'),
        ' with third parties for their own direct marketing purposes unless you',
        ' affirmatively agree to such disclosure. California customers can receive',
        ' more information about our compliance with this law or to make a request',
        ' under California Civil Code Section 1798.83 by sending us an email at',
        ' privacy@schoolhouse.world or by writing to us as set forth in ',
        ctx.linkInternal(getPrivacyUrl(TAGS.CONTACTING_SCHOOLHOUSE), '"Contacting Schoolhouse"'),
        ' section below:\n'
      ),
    ]),
  },
  {
    anchor: TAGS.CONTACTING_SCHOOLHOUSE,
    content: composeRichText(ctx => [
      ctx.heading('Contacting Schoolhouse'),
      ctx.paragraph(
        'If you have any questions about this Privacy Policy, our privacy',
        ' practices, or for any other purposes, please contact us by email at',
        ' privacy@schoolhouse.world. You may also write to us at the address below',
        ' and please email a copy:\n'
      ),
      ctx.paragraph('Schoolhouse'),
      ctx.paragraph('3790 El Camino Real'),
      ctx.paragraph('Unit #544'),
      ctx.paragraph('Palo Alto, CA 94306'),
    ]),
  },
]

function PrivacyPolicy(): ReactElement {
  return (
    <Layout title="Privacy Policy">
      <LinkableDiv id="highlights">
        <Box bg="bg.subtle">
          <Container maxWidth="768px">
            <Heading size="3xl" as="h1">
              Privacy Principles
            </Heading>
            <Text my="16px">Last Updated {LAST_UPDATED}</Text>
            <Box my="8px">
              <ShwLink isExternal href={getAboutUrl('trust-safety')}>
                <Button
                  variant="outline"
                  rightIcon={
                    <FontAwesomeIcon icon={faExternalLink} color={chakraTokenToVar('primary.fg')} />
                  }
                >
                  View Trust &amp; Safety Center
                </Button>
              </ShwLink>
            </Box>
            <RichText content={PRIVACY_PRINCIPLES} />
          </Container>
        </Box>
      </LinkableDiv>
      <LinkableDiv id="policy">
        <Container maxW="768px">
          <Heading size="3xl" as="h1">
            Privacy Policy
          </Heading>
          <Text my="16px">Last Updated {LAST_UPDATED}</Text>
          {PRIVACY_POLICY_SECTIONS.map(section => (
            <LinkableDiv id={section.anchor} key={section.anchor}>
              <RichText content={section.content} />
            </LinkableDiv>
          ))}
        </Container>
      </LinkableDiv>
    </Layout>
  )
}

const PrivacyPolicyPage = makePage(PrivacyPolicy, {
  onboardingUsers: 'allowed',
})
export const getServerSideProps = makeServerProps(PrivacyPolicyPage)
export default PrivacyPolicyPage
